import { message } from 'antd';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { env } from 'configs/EnvironmentConfig';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import axios from 'auth/FetchInterceptor';
import { API_BASE_URL } from 'configs/AppConfig';
import { useEffect, useState } from 'react';

export const uploadFiles = ({ files, id, api, field }) => {
	const formData = new FormData();
	files.forEach(file => {
		formData.append('files', file.originFileObj, file.name);
	});

	formData.append('ref', api);
	formData.append('refId', id);
	formData.append('field', field);

	return axios.post('/upload', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
};

export const downloadFile = url => {
	const token = localStorage.getItem(AUTH_TOKEN);
	return fetch(`${API_BASE_URL}/upload/download/${encodeURIComponent(url)}`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/octet-stream',
			Authorization: `Bearer ${token}`,
		},
	}).then(response => {
		// Clone the response before returning it to ensure we can read it multiple times
		const clonedResponse = response.clone();
		return clonedResponse;
	});
};

export const useDownloadFile = url => {
	const [data, setData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [toggle, setToggle] = useState(false);

	const refetch = () => {
		setToggle(!toggle);
	};

	useEffect(() => {
		setIsLoading(true);
		setData(null);

		downloadFile(url)
			.then(response => setData(response))
			.catch(error => setError(error))
			.finally(() => setIsLoading(false));
	}, [url]);

	return [
		data,
		{
			isLoading,
			error,
			refetch,
		},
	];
};

export const useLazyDownloadFile = () => {
	const [url, setUrl] = useState(null);
	const [result, setResult] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	const trigger = url => {
		setUrl(url);
		setIsLoading(true);
		setError(null);
	};

	useEffect(() => {
		if (!url) return;

		downloadFile(url)
			.then(response => {
				// Convert the response to a Blob immediately to ensure we're not trying to read the stream later
				return response.blob().then(blob => {
					// Create a wrapper object with a blob method that returns a Promise with the blob data
					return {
						blob: () => Promise.resolve(blob),
						contentType:
							response.headers.get('Content-Type') ||
							'application/octet-stream',
						size: blob.size,
						type: blob.type,
					};
				});
			})
			.then(data => setResult(data))
			.catch(error => {
				console.error('Download error:', error);
				setError(error);
			})
			.finally(() => setIsLoading(false));
	}, [url]);

	return [
		trigger,
		result,
		{
			isLoading,
			error,
		},
	];
};

// RTK Query
export const uploadApi = createApi({
	reducerPath: 'uploadApi',
	baseQuery: fetchBaseQuery({
		baseUrl: `${env.API_ENDPOINT_URL}${env.API_CLIENT_URL}`,
		prepareHeaders: (headers, { getState }) => {
			const token =
				localStorage.getItem(AUTH_TOKEN) || getState().auth.token;

			if (token) {
				headers.set('Authorization', `Bearer ${token}`);
			}
			return headers;
		},
	}),

	// default configuration
	refetchOnReconnect: true,
	refetchOnMountOrArgChange: true,
	refetchOnFocus: true,

	//endpoints
	endpoints: build => ({
		uploadFiles: build.mutation({
			query: ({ files, id, api, field, customName }) => {
				let formData = new FormData();
				files.forEach(file => {
					formData.append(
						'files',
						file.originFileObj,
						customName
							? `${customName}.${
									file.name.split('.')[file.name.split('.').length - 1]
							  }`
							: file.name
					);
				});
				formData.append('ref', api);
				formData.append('refId', id);
				formData.append('field', field);

				return {
					url: '/upload',
					method: 'POST',
					body: formData,
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Subida exitosa');
				} catch (error) {
					message.error('Error al subir archivo');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
		}),
	}),
});

export const { useUploadFilesMutation } = uploadApi;
